import {enableProdMode} from '@angular/core';
import * as Sentry from '@sentry/angular';
import {bootstrapApplication} from '@angular/platform-browser';
import {environment} from './environments/environment';
import {AppComponent} from './app/app.component';
import {appConfig} from './app/app.config';
import {
  scrubSentryBreadcrumb,
  scrubSentryEvent,
  scrubSentrySpan,
} from './app/shared/common/functions/sentry-scrub';
import {sampleSentryTrace} from './app/shared/common/functions/sentry-sampling';
import {SentryTracesSamplerContext} from './app/shared/models/sentry.model';

declare const SENTRY_RELEASE: string | undefined;

interface ConfigVariables {
  isMaintenanceMode: string;
  maintenancePageUrl: string;
}

const handleMaintenanceMode = (config: ConfigVariables): boolean => {
  const isMaintenance = String(config.isMaintenanceMode).toLowerCase() === 'true';
  if (!isMaintenance) {
    return false;
  }
  const maintenancePageUrl = (config.maintenancePageUrl || environment.defaultMaintenancePageUrl).trim();
  try {
    window.location.replace(new URL(maintenancePageUrl).toString());
    return true;
  } catch {
    console.error('Invalid maintenance URL:', maintenancePageUrl);
    return false;
  }
};

function initSentryAndBootstrap(): void {
  Sentry.init({
    environment: environment.name,
    ...environment.sentry,
    release: typeof SENTRY_RELEASE === 'undefined' ? undefined : SENTRY_RELEASE,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      graphQL: {
        document: true,
        variables: false,
      },
      genAI: {
        inputs: false,
        outputs: false,
      },
      databaseQueryData: false,
    },
    tracesSampler: (context: SentryTracesSamplerContext): number => sampleSentryTrace(
      context,
      environment.sentry.tracesSampleRate,
    ),
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeSendSpan: scrubSentrySpan,
    beforeBreadcrumb: scrubSentryBreadcrumb,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.breadcrumbsIntegration({console: false}),
      Sentry.extraErrorDataIntegration(),
    ],
  });
  if (environment.production) {
    enableProdMode();
  }
  bootstrapApplication(AppComponent, appConfig).catch(console.error);
}

fetch('/assets/app.config.json').then(async response => {
  if (!response.ok) {
    throw new Error(`Error loading configuration! Status: ${response.status}`);
  }
  const config = await response.json();
  if (handleMaintenanceMode(config)) {
    return;
  }
  initSentryAndBootstrap();
}).catch(error => {
  console.error('Error loading configuration:', error);
  initSentryAndBootstrap();
});

// Global Error Handlers

// Catch failed dynamic imports
window.addEventListener('unhandledrejection', event => {
  const message = event?.reason?.message ?? '';

  if (message.includes('Failed to fetch dynamically imported module')) {
    event.preventDefault(); // Hide error from user
    window.location.reload(); // Silent refresh
  }
});

// Catch other asset loading errors
window.addEventListener('error', event => {
  const message = event?.message ?? '';

  if (message.includes('Loading chunk') || message.includes('ChunkLoadError')) {
    event.preventDefault();
    window.location.reload();
  }
});
